import { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import SwitchToggle from "../../../components/SwitchToggle";
import AddEditToneConfig from "../../../Modals/addToneConfig";
import { Button } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import SearchBar from "../../../components/SearchBar";
import { useDeleteToneMutation, useLazyGetToneQuery } from "../../../services/toneConfig";
import { showError, showToast } from "../../../constants/toasts";
import DeleteConfirmModal from "../../../Modals/deleteConfirmModal";
import { BUDGET_TIER_NAME } from "../../../utils/enums";
import IfCan from "../../../components/IfCan";

const TonesList = () => {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<any[]>([]);
  const [getTone] = useLazyGetToneQuery();
  const [deleteTone] = useDeleteToneMutation();
  const [totalCount, setTotalCount] = useState<number>(0);
  const totalPages = Math.ceil(totalCount / 10);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const handleCloseModal = () => {
    setOpen(false);
    setEditId(null);
  };

  const handleEdit = (id: number) => {
    setEditId(id);
    setOpen(true);
  };

  const handleAdd = () => {
    setEditId(null);
    setOpen(true);
  };

  const handleSuccess = () => {
    getToneList();
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    try {
      await deleteTone({ id: selectedId }).unwrap();
      showToast("Tone deleted successfully");
      getToneList();
    } catch (e: any) {
      showError(e?.data?.message || "Failed to delete tone");
    }
    setDeleteOpen(false);
    setSelectedId(null);
  };

  const getToneList = async () => {
    setIsLoading(true);
    try {
      const response = await getTone({
        search: debouncedSearchTerm,
        start: page,
        length: 10
      }).unwrap();
      if (response?.status === 200) {
        setData(response?.data?.response_object);
        setTotalCount(response?.data?.total_records || 0);
      } else {
        setData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.log(error);
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getToneList();
  }, [page, debouncedSearchTerm]);

  return (
    <>
      <div className="table_header">
        <div className="left_s">
          <Box className="search_box">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              placeholder="Search..."
            />
          </Box>
        </div>
        <div className="right_s">
          <IfCan module="Budget Intelligence" action="add_edit">
            <Button className="btn btn_primary" onClick={handleAdd}>
              <AddIcon /> Add Tone
            </Button>
          </IfCan>
        </div>
      </div>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Budget Tier</TableCell>
              <TableCell>Tone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                  No tones found
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow hover key={row.id}>
                  <TableCell><strong>{BUDGET_TIER_NAME[row.budget_tier] || `Tier ${row.budget_tier}`}</strong></TableCell>
                  <TableCell>
                    <audio controls src={row.audio_tone?.media_file_url || undefined}>
                      <source src={row.audio_tone?.media_file_url || undefined} type={row.audio_tone?.file_type || "audio/mpeg"} />
                      Your browser does not support the audio element.
                    </audio>
                  </TableCell>
                  <TableCell>
                    <IfCan module="Budget Intelligence" action="add_edit">
                      <SwitchToggle />
                    </IfCan>
                  </TableCell>
                  <TableCell>
                    <Box className="table_actions v2">
                      <IfCan module="Budget Intelligence" action="add_edit">
                        <IconButton onClick={() => handleEdit(row.id)}>
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                      </IfCan>
                      <IfCan module="Budget Intelligence" action="delete">
                        <IconButton onClick={() => { setSelectedId(row.id); setDeleteOpen(true); }}>
                          <img src="/static/images/trash2_icon.svg" alt="Delete" />
                        </IconButton>
                      </IfCan>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AddEditToneConfig
        open={open}
        onClose={handleCloseModal}
        setOpen={setOpen}
        mode={editId ? "edit" : "add"}
        editId={editId}
        onSuccess={handleSuccess}
      />
      <DeleteConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        handleDelete={handleDelete}
        title="Are you sure you want to delete this tone?"
        message="This action cannot be undone."
      />
    </>
  );
};

export default TonesList;
